import React, { useState } from 'react';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { classnames } from 'Utils/classnames/classnames.utils';
import { SeeMore } from 'Components/frameworks/see-more/SeeMore';
import { TextLink } from 'Components/frameworks/text-link/TextLink';
import { CrisisAlert } from 'Components/widgets/crisis-alert/CrisisAlert';
import { TravelSherpaWidget } from './TravelSherpaWidget';
type Props = {
  className?: string;
  crisisAlert?: {
    link: {
      label: string;
      url: string;
    };
    message: string;
  };
  departureDate: number;
  duration: number;
  loadMore?: string;
  product?: Product;
  travelSherpa?: {
    disclaimer: string;
  };
};
export const TravelSherpa = ({
  className,
  crisisAlert,
  departureDate,
  duration,
  loadMore,
  product,
  travelSherpa = {},
}: Props) => {
  const [isLoadingWidget, setIsLoadingWidget] = useState(false);
  const { disclaimer } = travelSherpa;
  const track = useTracking();

  const onClickSeeMore = (_, isExpanded) => {
    setIsLoadingWidget(false);

    if (!isExpanded) {
      setIsLoadingWidget(true);
      track({
        actionGA: 'travelRestrictions',
        labelGA: 'checkCountryRestrictions',
      });
    }
  };

  const hasTravelSherpaWidget =
    !product || product.travelSherpaOutbound !== product.travelSherpaInbound;

  if (!hasTravelSherpaWidget && !crisisAlert?.message) {
    return null;
  }

  const messageWhenWidgetFail = (
    <div className="py-10">
      <p className="pe-10">{t('travelRestriction.errorMessage.label')}</p>
      <TextLink
        className="text-black"
        data-testid="TravelSherpaLinkFail"
        label={t('travelRestriction.errorMessage.linkLabel')}
        onClick={() =>
          track({
            actionGA: 'travelRestrictions',
            categoryGA: 'resort',
            labelGA: 'errorMessage_link',
          })
        }
        target="_blank"
        url={t('travelRestriction.errorMessage.link')}
      />
    </div>
  );
  return (
    <div className={classnames('md:flex', className)}>
      <div className="flex justify-center">
        <img alt="" className="h-[135px] w-200 flex-none" src={t('travelRestriction.iconLink')} />
      </div>
      {crisisAlert?.message ? (
        <div>
          <SeeMore
            className="mt-10 flex-1 md:mt-0"
            classNameButton="text-end"
            hasDynamicHeight={hasTravelSherpaWidget}
            hasNoScrollOnCollapse={!hasTravelSherpaWidget}
            isCollapseEnabled
            onClick={onClickSeeMore}
            seeLessLabel={t('common.loadLess')}
            seeMoreLabel={loadMore}
            seeMoreSize={90}
            seeMoreSizeTolerance={0}
          >
            <CrisisAlert
              icon={ICONS.WARNING}
              linkLabel={crisisAlert.link?.label}
              linkUrl={crisisAlert.link?.url}
              message={crisisAlert.message}
              onClick={() =>
                track({
                  actionGA: 'travelRestrictions',
                  categoryGA: 'resort',
                  labelGA: 'learnMore',
                })
              }
            />
            {hasTravelSherpaWidget && (
              <TravelSherpaWidget
                className="mt-25 md:mt-30"
                disclaimer={disclaimer}
                isLoadingWidget={isLoadingWidget}
                product={product}
                departureDate={departureDate}
                duration={duration}
              />
            )}
          </SeeMore>
          {messageWhenWidgetFail}
        </div>
      ) : (
        <div>
          {messageWhenWidgetFail}
          <TravelSherpaWidget
            className="flex-1"
            disclaimer={disclaimer}
            isLoadingWidget
            product={product}
            departureDate={departureDate}
            duration={duration}
          />
        </div>
      )}
    </div>
  );
};
